// React components
import { useState, useEffect, useContext } from "react";
import partCategoryService from "services/part-category-service";

// MUI and Material Dashboard Components
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';



export default function MainAttributeDialog({
    handleClose,
    open,
    id,
    attNum,
    numberOfAttributes,
    attributes,
    editMode = false,
    attributeData
}) {
    const [enableCondAttrs, setEnableCondAttrs] = useState(false);
    const [condAttRules, setCondAttRules] = useState({});
    const [attribute, setAttribute] = useState({});
    const [disableField, setDisableField] = useState({
        options: false,
        description: false,
        units: false
    })

    // This state is used to store errors that are not field related
    const [errorMessage, setErrorMessage] = useState({
        display: false,
        text: ""
    });
    // This state is used to store field related errors
    const [error, setError] = useState({
        attribute: false,
        options: false,
        description: false,
        units: false
    });


    // Fetches attribute data from the props and sets the initial states.
    useEffect(() => {
        setAttribute(attributeData);
        if (attributeData?.enableCondAttrs) {
            setEnableCondAttrs(attributeData.enableCondAttrs);
            const optionsArray = attributeData.options.split(';').map(opt => opt.trim());
            const rules = {};
            optionsArray.forEach(opt => {
                if (attributeData[opt]) {
                    rules[opt] = attributeData[opt];
                }
            });
            setCondAttRules(rules);
        }
    }, [attributeData]);

    useEffect(() => { // Reset the rules when enabling/disabling conditional attributes
        if (!enableCondAttrs) {
            setCondAttRules({});
        }
    }, [enableCondAttrs])

    useEffect(() => {
        const hasOptionsOrDesc = attribute.options || attribute.description;
        const hasUnits = attribute.units;
        setDisableField({
            options: hasUnits,
            description: hasUnits,
            units: hasOptionsOrDesc
        });
    }, [attribute])

    // Validator function to check for missing or invalid fields
    const checkErrors = () => {

        // Checks if the attribute name is provided
        // Checks if the description is provided when options are given and vice versa
        const newErrors = {
            attribute: !attribute.attribute ? "This field is required" : false,
            description: attribute.options && !attribute.description ? "Description is required when options are provided" : false,
            options: !attribute.options && attribute.description ? "Options are required when description is provided" : false,
            units: false
        };

        // Check if options and descriptions have matching lengths when split
        if (attribute.options && attribute.description) {
            const optionsArr = attribute.options.split(';').map(o => o.trim()).filter(o => o);
            const descArr = attribute.description.split(';').map(d => d.trim()).filter(d => d);

            if (optionsArr.length !== descArr.length) {
                newErrors.options = "Number of options must match number of descriptions";
                newErrors.description = "Number of descriptions must match number of options";
            }
        }

        // Validate that options do not contain dot characters
        if (attribute.options) {
            if (newErrors.options === false && attribute.options.includes('.')) {
                newErrors.options = "Options cannot contain dots ('.').";
            }
        }

        setError(newErrors);
        return Object.values(newErrors).every(err => err === false);
    }

    // Function to determine the field type based on the attribute's propreties
    // This is used to determine how the attribute will be rendered in the preview
    const findFieldType = (attribute) => {
        let newFieldType = "";

        if (attribute.options) {
            newFieldType = "select";
        } else if (attribute.attribute && attribute.units) {
            newFieldType = "text-with-units";
        } else {
            newFieldType = "text";
        }

        return newFieldType;
    }

    // Sanitizes the options string by trimming whitespace and ensuring proper formatting
    const sanitizeOptions = (options) => {
        if (typeof options !== "string") return options;
        return options.trim().split(";").map(opt => opt.trim()).join(";");
    }


    // Handles changes in the attribute fields
    const attChangeHandler = (e) => {
        setAttribute({
            ...attribute,
            [e.target.name]: e.target.value
        })
    }

    // Handles changes in the conditional attribute rules
    const ruleChangeHandler = (e) => {
        setCondAttRules({
            ...condAttRules,
            [e.target.name]: e.target.value
        });
    }

    // Exit on cancel/clicking outside the dialog
    const handleCloseDialog = () => {
        setEnableCondAttrs(false);
        setAttribute({});
        setError({
            attribute: false,
            options: false,
            description: false,
            units: false
        })
        setErrorMessage({ display: false })
        handleClose();
    }

    const handleCondAttrs = () => {
        if (!enableCondAttrs) {
            if (!attribute.options || !attribute.description) {
                setError({
                    ...error,
                    options: !attribute.options ? "Options are required for conditional attributes" : false,
                    description: !attribute.description ? "Description is required for conditional attributes" : false
                });
                return;
            } else {
                setError({
                    attribute: false,
                    options: false,
                    description: false,
                    units: false
                })
                if (!attributes || Object.keys(attributes).length <= 1) {
                    setErrorMessage({
                        display: true,
                        text: "At least one attribute other than type must be defined before enabling conditional attributes"
                    });
                } else {
                    // Validate that options do not contain dot characters before enabling conditional attributes
                    if (attribute.options && attribute.options.includes('.')) {
                        setError({
                            ...error,
                            options: "Options cannot contain dots ('.')."
                        });
                        return;
                    }
                    setErrorMessage({ display: false })
                    setEnableCondAttrs(true);
                }
            }
        } else {
            setEnableCondAttrs(false);
        }

    }

    // Exit on save
    const clickSaveHandler = async () => {
        // Check for errors before saving
        if (!checkErrors()) return;

        // Reset the fields for next use
        setEnableCondAttrs(false);
        setAttribute({});
        let toSubmitAtts = {};

        if (condAttRules) {
            const sortedCondAttrs = Object.fromEntries(
                Object.entries(condAttRules).map(([key, value]) => [
                    key,
                    value.split(';').map(Number).sort((a, b) => a - b).join(';')
                ])
            );
            toSubmitAtts = { ...attribute, ...sortedCondAttrs, enableCondAttrs: enableCondAttrs, fieldType: findFieldType(attribute) };
        } else {
            toSubmitAtts = { ...attribute, enableCondAttrs: enableCondAttrs, fieldType: findFieldType(attribute) };
        }

        if (toSubmitAtts.options) {
            toSubmitAtts.options = sanitizeOptions(toSubmitAtts.options);
        }

        // Only propagate to backend if not in edit mode
        if (!editMode) {
            handleClose(toSubmitAtts, attNum);
        } else {
            // Prepare the payload for the API call
            const payload = {
                data: {
                    type: "partCategory",
                    attributes: {
                        key: attNum,
                        value: toSubmitAtts
                    }
                }
            };
            // Call the API to edit the attribute
            await partCategoryService.editAttribute(payload, id);
            handleClose(toSubmitAtts, attNum);
        }
    }
    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            {/* Dialog content */}
            <DialogContent>
                <MDBox m={2} mb={0}>
                    {/* Title and instructions */}
                    <MDBox mb={2} mt={2}>
                        <MDTypography variant="h5" fontWeight="bold" mt={1}>
                            Define this part category's type selection :
                        </MDTypography>
                        <DialogContentText>
                            Please input the options, description and units.
                            When entering multiple values in the options, description or units field please seperate the values with a semi-colon (;).
                            Please note that for conditional attributes generation, the options and description fields must be filled.
                        </DialogContentText>
                    </MDBox>

                    {/* Define attribute */}
                    <MDBox>
                        {/* Attribute Name */}
                        <Grid container spacing={3} mb={1}>
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    key={`attribute`}
                                    label={`Attribute Name`}
                                    value={"TYPE"}
                                    name={`attribute`}
                                    fullWidth
                                    error={!!error.attribute}
                                    inputProps={{
                                        autoComplete: "description",
                                        form: {
                                            autoComplete: "off",
                                        },
                                    }}
                                    disabled
                                    onChange={attChangeHandler}
                                />
                                {error.attribute && (
                                    <MDTypography variant="caption" color="error">
                                        {error.attribute}
                                    </MDTypography>
                                )}
                            </Grid>
                            {/* Attribute Options */}
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    key={`options`}
                                    label={`Attribute Options`}
                                    value={attribute[`options`] || ''}
                                    name={`options`}
                                    fullWidth
                                    error={!!error.options}
                                    inputProps={{
                                        autoComplete: "examples",
                                        form: {
                                            autoComplete: "off",
                                        },
                                    }}
                                    disabled={enableCondAttrs || disableField.options}
                                    onChange={attChangeHandler}
                                />
                                {error.options && (
                                    <MDTypography variant="caption" color="error">
                                        {error.options}
                                    </MDTypography>
                                )}
                            </Grid>
                        </Grid>
                        <Grid container spacing={3}>
                            {/* Attribute's option description */}
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    key={`description`}
                                    label={`Attribute Options Description`}
                                    value={attribute[`description`] || ''}
                                    name={`description`}
                                    fullWidth
                                    error={!!error.description}
                                    inputProps={{
                                        autoComplete: "description",
                                        form: {
                                            autoComplete: "off",
                                        },
                                    }}
                                    disabled={enableCondAttrs || disableField.description}
                                    onChange={attChangeHandler}
                                />
                                {error.description && (
                                    <MDTypography variant="caption" color="error">
                                        {error.description}
                                    </MDTypography>
                                )}
                            </Grid>
                            {/* Attribute Units */}
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    key={`units`}
                                    label={`Attribute Units`}
                                    value={attribute[`units`] || ''}
                                    name={`units`}
                                    fullWidth
                                    error={!!error.units}
                                    inputProps={{
                                        autoComplete: "description",
                                        form: {
                                            autoComplete: "off",
                                        },
                                    }}
                                    disabled={enableCondAttrs || disableField.units}
                                    onChange={attChangeHandler}
                                />
                                {error.units && (
                                    <MDTypography variant="caption" color="error">
                                        {error.units}
                                    </MDTypography>
                                )}
                            </Grid>
                        </Grid>
                        {errorMessage.display && (
                            <MDTypography variant="caption" color="error">
                                {errorMessage.text}
                            </MDTypography>
                        )}
                    </MDBox>
                    {/* Define Attributes Conditional Gen */}
                    {enableCondAttrs && attribute[`options`] && (
                        <MDBox mt={2}>
                            <MDTypography variant="h5" fontWeight="bold" mt={1}>
                                Define the Attribute's Conditional Generation :
                            </MDTypography>
                            <DialogContentText>
                                Please select for each option, the attributes tied to it.
                            </DialogContentText>
                            <Grid container spacing={1}>
                                {(attribute[`options`] || '').split(';').map((value, index, array) => (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={index === array.length - 1 && array.length % 2 !== 0 ? 12 : 6}
                                        key={`${value.trim()}-${index}`}
                                    >
                                        <FormControl sx={{ m: 1, width: '100%' }}>
                                            <InputLabel id={`select-label-${value.trim()}`}>{value.trim()}</InputLabel>
                                            <Select
                                                labelId={`select-label-${value.trim()}`}
                                                input={<OutlinedInput label={value.trim()} />}
                                                value={(condAttRules[value.trim()]?.includes('0') ?
                                                    condAttRules[value.trim()]?.split(';').map(Number) :
                                                    ['0', ...(condAttRules[value.trim()]?.split(';') || [])].map(Number)) || []}
                                                name={value.trim()}
                                                onChange={(e) => ruleChangeHandler({
                                                    target: {
                                                        name: value.trim(),
                                                        value: e.target.value.join(';')
                                                    }
                                                })}
                                                multiple
                                                fullWidth
                                                renderValue={(selected) => selected.sort((a, b) => a - b).join(', ')}
                                                sx={{ height: '45px', '& .MuiSelect-select': { paddingY: '8px' } }}
                                            >
                                                {[...Array(numberOfAttributes)]
                                                    .map((_, i) => attributes[i] && (
                                                        <MenuItem
                                                            key={i}
                                                            value={i}
                                                            disabled={i === 0}
                                                        >
                                                            {i} - {attributes[i].attribute}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ))}
                            </Grid>
                        </MDBox>
                    )}
                </MDBox>
            </DialogContent>
            {/* Dialog footer (actions) */}
            <DialogActions>
                <MDBox display="flex" justifyContent="space-between" width="100%">
                    <MDBox>
                        <MDButton color="dark" variant="gradient" size="small" onClick={handleCondAttrs}>
                            {enableCondAttrs ? "Edit Type Definition" : "Edit Conditional Attributes"}
                        </MDButton>
                    </MDBox>
                    <MDBox display="flex">
                        <MDBox mr={1}><MDButton color="dark" variant="gradient" size="small" onClick={handleCloseDialog}>Cancel</MDButton></MDBox>
                        <MDBox><MDButton color="dark" variant="gradient" size="small" onClick={clickSaveHandler}>Save</MDButton></MDBox>
                    </MDBox>
                </MDBox>
            </DialogActions>
        </Dialog>
    )
}
